/**
 * @fileoverview Template-editor field-mappings tab content for the import wizard.
 * @module import-editor/components
 *
 * Responsibilities:
 * - Render an optional sample-file drop zone that parses the file and seeds column names.
 * - Render the MappingSummaryPanel (stats, AI-suggest, sample data, ignored columns).
 * - Render the existing mappings table with edit / reorder / delete actions.
 * - Provide an "Add Mapping" entry point that opens the mapping slide-out.
 *
 * Dependencies:
 * - React (useState, useCallback, useEffect, useRef)
 * - lucide-react icons
 * - TemplateEditorState type from ../templateEditor/types
 * - FieldMapping type from @firesquirrel/local-db
 * - getTargetFieldLabel, resolveTargetFieldValue from ../utils/targetFieldOptions
 * - MappingSummaryPanel for the expandable stats / AI / sample-data panel
 *
 * Architecture:
 * - Presentational: all data persistence flows through props (onChange, onFileParsed).
 * - No Dexie reads/writes, no direct API calls.
 * - AI-suggestion state is local until the user accepts a suggestion.
 *
 * Notes:
 * - The auto-suggest banner and inline pending-suggestions list have been
 *   replaced by the expandable MappingSummaryPanel to preserve mapping-table real estate.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Key, Loader2, Upload, FileUp, FileCheck2 } from 'lucide-react';
import type { TemplateEditorState } from '../templateEditor/types';
import type { FieldMapping } from '@firesquirrel/local-db';
import { getTargetFieldLabel, resolveTargetFieldValue } from '../utils/targetFieldOptions';
import { MappingSummaryPanel } from './MappingSummaryPanel';

// ---------------------------------------------------------------------------
// Service interfaces — injected by the consuming app to avoid coupling
// ---------------------------------------------------------------------------

export interface ParseResult {
  fileType: string;
  columns: string[];
  sampleRows: Record<string, unknown>[];
  totalRows: number;
  availableSheets?: string[];
  activeSheet?: string;
}

export interface MappingSuggestion {
  sourceColumn: string;
  /** When present, this suggestion uses multiple source columns (e.g. concatenation). */
  sourceColumns?: string[];
  /** Separator to use when transform is 'concat'. Defaults to ' '. */
  concatSeparator?: string;
  targetField: string;
  transform: string;
  confidence: number;
  mappingSource?: 'ai' | 'rule' | 'manual' | 'template' | 'pattern';
  reasoning?: string;
}

export function getSuggestionMappingSource(
  suggestion: MappingSuggestion,
): 'ai' | 'rule' | 'manual' | 'template' | 'pattern' {
  return suggestion.mappingSource ?? 'rule';
}

export interface FieldMappingsTabContentProps {
  state: TemplateEditorState;
  onChange: (patch: Partial<TemplateEditorState>) => void;
  onEditMapping: (index: number | null) => void;
  errors?: string[];
  availableColumns?: string[];
  sampleRows?: Record<string, unknown>[];
  onFileParsed?: (columns: string[], rows: Record<string, unknown>[]) => void;
  /**
   * Fires every time the user chooses a sample file, BEFORE parsing
   * completes. The host captures the File reference so it can hand it
   * off to the run-flow container (Card 3 save-and-import path) with
   * no re-upload required.
   */
  onFileSelected?: (file: File) => void;
  /** Injected by the app — parse a File and return columns+rows */
  parseFile?: (file: File, sampleSize?: number, sheetName?: string) => Promise<ParseResult>;
  /** Injected by the app — fetch AI/rule mapping suggestions.
   *  May also report `tier2Used` / `tier2Error` so the UI can flag
   *  AI-matcher failures (Item 1, 2026-05-08): the server soft-fails
   *  Tier 2 — Tier 1 still returns — but the wizard MUST display the
   *  AI failure so users understand why columns went unmatched. */
  suggestMappings?: (params: {
    dataMode: string;
    columns: string[];
    sampleRows?: Record<string, unknown>[];
    tier?: string;
  }) => Promise<{
    suggestions: MappingSuggestion[];
    tier2Used?: boolean;
    tier2Error?: string;
  }>;
}

export default function FieldMappingsTabContent({
  state,
  onChange,
  onEditMapping,
  errors = [],
  availableColumns: availableColumnsProp,
  sampleRows: sampleRowsProp = [],
  onFileParsed,
  onFileSelected,
  parseFile,
  suggestMappings,
}: FieldMappingsTabContentProps) {
  const [suggestions, setSuggestions] = useState<MappingSuggestion[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  // Item 1 (2026-05-08): AI matcher failure surface. `aiTier2Used` records
  // whether the server attempted Tier 2 on the last call; `aiTier2Error`
  // captures the server's reason for any Tier 2 failure (missing creds,
  // HTTP non-200, parse error, network error). Distinct from
  // `suggestError` because the suggestion fetch itself may have succeeded
  // (Tier 1 returned suggestions) while Tier 2 silently failed.
  const [aiTier2Used, setAiTier2Used] = useState<boolean>(false);
  const [aiTier2Error, setAiTier2Error] = useState<string | null>(null);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());

  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [fileParseLoading, setFileParseLoading] = useState(false);
  const [fileParseError, setFileParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!parseFile) return;

    const guardFileDrop = (event: DragEvent) => {
      if (!event.dataTransfer?.types?.includes('Files')) return;
      event.preventDefault();
    };

    document.addEventListener('dragover', guardFileDrop);
    document.addEventListener('drop', guardFileDrop);

    return () => {
      document.removeEventListener('dragover', guardFileDrop);
      document.removeEventListener('drop', guardFileDrop);
    };
  }, [parseFile]);

  const handleSampleFile = useCallback(async (file: File, sheetName?: string) => {
    if (!parseFile) return;
    // Surface the File up front (before the parse finishes) so the
    // host can capture the reference even if the user moves on before
    // parsing completes. Called on every selection, including sheet
    // changes on the same file — hosts should treat it as idempotent.
    onFileSelected?.(file);
    setFileParseLoading(true);
    setFileParseError(null);
    try {
      const result = await parseFile(file, 10, sheetName);
      setParseResult(result);
      const cols = result.columns ?? [];
      const rows = result.sampleRows ?? [];
      onFileParsed?.(cols, rows);
    } catch (err: any) {
      setFileParseError(err?.message ?? 'Failed to parse file');
    } finally {
      setFileParseLoading(false);
    }
  }, [parseFile, onFileParsed, onFileSelected]);

  const [sampleFile, setSampleFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const acceptSampleFile = useCallback((file: File) => {
    setSampleFile(file);
    setParseResult(null);
    handleSampleFile(file);
  }, [handleSampleFile]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    acceptSampleFile(file);
  }, [acceptSampleFile]);

  const handleDropZoneDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (fileParseLoading) return;
    setIsDragOver(true);
  }, [fileParseLoading]);

  const handleDropZoneDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDropZoneDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (fileParseLoading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) acceptSampleFile(file);
  }, [acceptSampleFile, fileParseLoading]);

  const handleSheetChange = useCallback((sheet: string) => {
    if (!sampleFile) return;
    handleSampleFile(sampleFile, sheet);
  }, [sampleFile, handleSampleFile]);

  const availableColumns = (parseResult?.columns?.length ? parseResult.columns : availableColumnsProp);
  const sampleRows = parseResult?.sampleRows?.length ? parseResult.sampleRows : sampleRowsProp;

  const removeRow = (i: number) => {
    onChange({ fieldMappings: state.fieldMappings.filter((_, idx) => idx !== i) });
  };

  const moveRow = (i: number, dir: -1 | 1) => {
    const arr = [...state.fieldMappings];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j]!, arr[i]!];
    onChange({ fieldMappings: arr });
  };

  const transformLabel = (m: FieldMapping) => {
    const t = m.transform?.type ?? 'none';
    if (t === 'none') return '—';
    return t;
  };

  const mappedSourceColumns = new Set(state.fieldMappings.flatMap(m => m.sourceColumns));

  /** Columns from the parsed file not yet assigned to any target field. */
  const unmappedSourceCols = (availableColumns ?? []).filter(c => !mappedSourceColumns.has(c));

  /** Count of mappings that have either a source column or a constant default value. */
  const mappedCount = state.fieldMappings.filter(m => m.sourceColumns?.[0] || (m.defaultValue && m.defaultValue.length > 0)).length;

  const suggestionSourceCols = (s: MappingSuggestion): string[] =>
    s.sourceColumns ?? [s.sourceColumn];

  const pendingSuggestions = suggestions.filter(
    s => suggestionSourceCols(s).every(col => !dismissedSuggestions.has(col) && !mappedSourceColumns.has(col)),
  );

  const handleAutoSuggest = useCallback(async () => {
    if (!suggestMappings) return;
    const columns = availableColumns ?? [];
    if (columns.length === 0) return;
    setSuggestLoading(true);
    setSuggestError(null);
    setAiTier2Error(null);
    setAiTier2Used(false);
    try {
      const result = await suggestMappings({
        dataMode: state.dataMode,
        columns,
        sampleRows,
        tier: 'both',
      });
      setSuggestions(result.suggestions);
      setDismissedSuggestions(new Set());
      // Item 1: capture the AI matcher's per-call status so the banner
      // can render a distinct "AI unavailable" message even though the
      // overall fetch succeeded with Tier 1 suggestions.
      setAiTier2Used(!!result.tier2Used);
      setAiTier2Error(result.tier2Error ?? null);
    } catch (err: any) {
      setSuggestError(err?.message ?? 'Failed to fetch suggestions');
    } finally {
      setSuggestLoading(false);
    }
  }, [availableColumns, sampleRows, state.dataMode, suggestMappings]);

  const acceptSuggestion = (s: MappingSuggestion) => {
    const resolvedTargetField = resolveTargetFieldValue(s.targetField, state.dataMode);
    if (!resolvedTargetField) return;
    const cols = suggestionSourceCols(s);
    const newMapping: FieldMapping = {
      targetField: resolvedTargetField,
      sourceColumns: cols,
      isMatchKey: false,
      transform: {
        type: (s.transform as any) ?? 'none',
        ...(s.concatSeparator ? { separator: s.concatSeparator } : {}),
      },
      confidenceScore: s.confidence,
      mappingSource: getSuggestionMappingSource(s),
    };
    onChange({ fieldMappings: [...state.fieldMappings, newMapping] });
    setDismissedSuggestions(prev => new Set([...prev, ...cols]));
  };

  const dismissSuggestion = (s: MappingSuggestion) => {
    setDismissedSuggestions(prev => new Set([...prev, ...suggestionSourceCols(s)]));
  };

  const acceptAllHigh = () => {
    const highConf = pendingSuggestions.filter(s => s.confidence >= 0.8);
    const newMappings: FieldMapping[] = highConf.flatMap(s => {
      const resolvedTargetField = resolveTargetFieldValue(s.targetField, state.dataMode);
      if (!resolvedTargetField) return [];
      return [{
        targetField: resolvedTargetField,
        sourceColumns: suggestionSourceCols(s),
        isMatchKey: false,
        transform: {
          type: (s.transform as any) ?? 'none',
          ...(s.concatSeparator ? { separator: s.concatSeparator } : {}),
        },
        confidenceScore: s.confidence,
        mappingSource: getSuggestionMappingSource(s),
      }];
    });
    onChange({ fieldMappings: [...state.fieldMappings, ...newMappings] });
    setDismissedSuggestions(prev => new Set([...prev, ...highConf.flatMap(suggestionSourceCols)]));
  };

  return (
    <div className="space-y-3">
      {errors.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 space-y-1">
          {errors.map(e => (
            <p key={e} className="text-xs text-red-700">• {e}</p>
          ))}
        </div>
      )}

      {/* Sample file drop zone — only shown when no columns pre-supplied and parseFile is available */}
      {!availableColumnsProp && parseFile && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload a sample file by dragging it here or clicking to browse"
          onDragOver={handleDropZoneDragOver}
          onDragEnter={handleDropZoneDragOver}
          onDragLeave={handleDropZoneDragLeave}
          onDrop={handleDropZoneDrop}
          onClick={() => !fileParseLoading && fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !fileParseLoading) {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          className={`group relative overflow-hidden rounded-xl border-2 border-dashed px-6 py-8 text-center transition-all duration-200 ease-out cursor-pointer focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
            fileParseLoading
              ? 'border-blue-300 bg-blue-50/50 cursor-wait'
              : isDragOver
              ? 'border-blue-500 bg-blue-50 scale-[1.01] shadow-md'
              : sampleFile && !fileParseError
              ? 'border-emerald-300 bg-emerald-50/60 hover:border-emerald-400 hover:bg-emerald-50'
              : fileParseError
              ? 'border-red-300 bg-red-50/60 hover:border-red-400'
              : 'border-gray-300 bg-gray-50/50 hover:border-blue-400 hover:bg-blue-50/40 hover:shadow-xs'
          }`}
        >
          <div className="flex flex-col items-center justify-center gap-3">
            {/* Icon with animation */}
            <div
              className={`flex h-14 w-14 items-center justify-center rounded-full transition-all duration-200 ${
                fileParseLoading
                  ? 'bg-blue-100 text-blue-600'
                  : isDragOver
                  ? 'bg-blue-100 text-blue-600 scale-110'
                  : sampleFile && !fileParseError
                  ? 'bg-emerald-100 text-emerald-600'
                  : fileParseError
                  ? 'bg-red-100 text-red-600'
                  : 'bg-white text-gray-400 group-hover:bg-blue-100 group-hover:text-blue-600 group-hover:scale-105'
              }`}
            >
              {fileParseLoading ? (
                <Loader2 className="h-7 w-7 animate-spin" />
              ) : sampleFile && !fileParseError ? (
                <FileCheck2 className="h-7 w-7" />
              ) : isDragOver ? (
                <FileUp className="h-7 w-7 animate-bounce" />
              ) : (
                <Upload className="h-7 w-7 transition-transform duration-200 group-hover:-translate-y-0.5" />
              )}
            </div>

            {/* Primary message */}
            <div className="space-y-1">
              {fileParseLoading ? (
                <p className="text-sm font-semibold text-blue-700">Parsing your file…</p>
              ) : isDragOver ? (
                <p className="text-sm font-semibold text-blue-700">Release to upload</p>
              ) : sampleFile && !fileParseError ? (
                <>
                  <p className="text-sm font-semibold text-emerald-700">
                    <span className="truncate inline-block max-w-[32ch] align-bottom">{sampleFile.name}</span>
                  </p>
                  <p className="text-xs text-emerald-700/80">
                    {parseResult
                      ? `${parseResult.columns.length} columns · ${parseResult.totalRows.toLocaleString()} rows detected`
                      : 'Parsed successfully'}
                  </p>
                  <p className="text-xs text-gray-500 pt-1">Drop another file or click to replace</p>
                </>
              ) : fileParseError ? (
                <>
                  <p className="text-sm font-semibold text-red-700">Couldn&apos;t parse that file</p>
                  <p className="text-xs text-red-600">{fileParseError}</p>
                  <p className="text-xs text-gray-500 pt-1">Drop another file or click to try again</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-gray-800">
                    Drag &amp; drop a sample file here
                  </p>
                  <p className="text-xs text-gray-600">
                    or <span className="text-blue-600 font-medium group-hover:underline">click to browse</span>
                  </p>
                </>
              )}
            </div>

            {/* Supporting detail */}
            {!sampleFile && !fileParseLoading && !isDragOver && (
              <div className="mt-1 max-w-md space-y-1.5">
                <p className="text-xs text-gray-500 leading-relaxed">
                  We&apos;ll parse the first few rows to auto-detect column names, power AI mapping suggestions,
                  and preview how your data will import.
                </p>
                <p className="text-[11px] text-gray-400">
                  Supports CSV, Excel (.xlsx, .xls), GeoPackage, GeoJSON, JSON, and zipped shapefiles
                </p>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".csv,.xlsx,.xls,.gpkg,.geojson,.zip,.json"
            onChange={handleFileInput}
          />
        </div>
      )}

      {/* Sheet selector */}
      {parseResult && (parseResult.availableSheets ?? []).length > 1 && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <label className="text-xs font-semibold text-amber-800 shrink-0">Sheet:</label>
          <select
            className="border border-amber-300 rounded-md px-2 py-1 text-xs text-gray-800 bg-white focus:outline-hidden focus:ring-1 focus:ring-amber-400"
            value={parseResult.activeSheet ?? ''}
            onChange={e => handleSheetChange(e.target.value)}
            disabled={fileParseLoading}
          >
            {(parseResult.availableSheets ?? []).map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <span className="text-[11px] text-amber-700">
            {(parseResult.availableSheets ?? []).length} sheets detected — select which to use for mapping.
          </span>
          {fileParseLoading && <Loader2 className="h-3 w-3 animate-spin text-amber-500" />}
        </div>
      )}

      {/* Mapping summary panel — stats, AI-suggest, sample data, ignored columns */}
      {availableColumns && availableColumns.length > 0 && (
        <MappingSummaryPanel
          mappedCount={mappedCount}
          availableColumns={availableColumns}
          unmappedSourceColumns={unmappedSourceCols}
          sampleRows={sampleRows}
          pendingSuggestions={pendingSuggestions}
          suggestLoading={suggestLoading}
          suggestError={suggestError}
          aiWarning={aiTier2Error}
          onSuggest={suggestMappings ? handleAutoSuggest : undefined}
          onAcceptSuggestion={acceptSuggestion}
          onDismissSuggestion={dismissSuggestion}
          onAcceptAllHigh={acceptAllHigh}
          dataMode={state.dataMode as any}
        />
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          {state.fieldMappings.length} field mapping{state.fieldMappings.length !== 1 ? 's' : ''} defined.
        </p>
        <button
          type="button"
          onClick={() => onEditMapping(null)}
          data-testid="add-mapping-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-md transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Mapping
        </button>
      </div>

      {state.fieldMappings.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center text-gray-400 text-sm">
          No field mappings yet. Click "Add Mapping" to define how columns in your file map to FireSight fields.
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full text-xs text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-2 font-medium text-gray-700 w-8 text-center">#</th>
                <th className="px-3 py-2 font-medium text-gray-700">Target Field</th>
                <th className="px-3 py-2 font-medium text-gray-700">Source Column(s)</th>
                <th className="px-3 py-2 font-medium text-gray-700">Transform</th>
                <th className="px-3 py-2 font-medium text-gray-700 w-20 text-center">Match Key</th>
                <th className="px-3 py-2 font-medium text-gray-700 w-28 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {state.fieldMappings.map((m, i) => (
                <tr key={i} className={i % 2 === 1 ? 'bg-gray-50/60 border-t border-gray-100' : 'border-t border-gray-100'}>
                  <td className="px-3 py-2 text-center text-gray-400">{i + 1}</td>
                  <td className="px-3 py-2 font-medium text-gray-800">
                    <span title={m.targetField}>
                      {getTargetFieldLabel(m.targetField, state.dataMode)}
                    </span>
                    {m.targetField !== getTargetFieldLabel(m.targetField, state.dataMode) && (
                      <span className="ml-1 text-gray-400 font-normal">({m.targetField})</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">{m.sourceColumns.join(', ')}</td>
                  <td className="px-3 py-2 text-gray-500">{transformLabel(m)}</td>
                  <td className="px-3 py-2 text-center">
                    {m.isMatchKey && (
                      <span className="inline-flex items-center gap-1 text-blue-700 font-medium">
                        <Key className="h-3 w-3" />
                        Key
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-1">
                      <button type="button" onClick={() => onEditMapping(i)} aria-label={`Edit mapping ${i + 1}`} className="p-0.5 rounded-sm text-gray-400 hover:text-gray-700">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => moveRow(i, -1)} disabled={i === 0} aria-label="Move up" className="p-0.5 rounded-sm text-gray-400 hover:text-gray-700 disabled:opacity-30">
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => moveRow(i, 1)} disabled={i === state.fieldMappings.length - 1} aria-label="Move down" className="p-0.5 rounded-sm text-gray-400 hover:text-gray-700 disabled:opacity-30">
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => removeRow(i)} aria-label={`Delete mapping ${i + 1}`} className="p-0.5 rounded-sm text-red-400 hover:text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
